import React from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Container, Typography, Button, Card, CardContent,
  Paper, Chip
} from '@mui/material';
import ReactPlayer from 'react-player';
import { motion } from 'framer-motion';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LockIcon from '@mui/icons-material/Lock';

const eventData = {
  'solar-eclipse-aquarius': {
    title: 'Annular Solar Eclipse in Aquarius',
    date: 'Feb 17th',
    freeVideoId: '4_UX8QJj09s',
    extendedVideoId: 'YOUR_SOLAR_ECLIPSE_AQUARIUS_EXTENDED_VIDEO_ID'
  },
  'lunar-eclipse-virgo': {
    title: 'Total Lunar Eclipse in Virgo',
    date: 'Mar 3rd',
    freeVideoId: 'fPLyXQvFThw',
    extendedVideoId: 'YOUR_LUNAR_ECLIPSE_VIRGO_EXTENDED_VIDEO_ID'
  },
  'solar-eclipse-leo': {
    title: 'Total Solar Eclipse in Leo',
    date: 'Aug 12th',
    freeVideoId: 'YOUR_SOLAR_ECLIPSE_LEO_FREE_VIDEO_ID',
    extendedVideoId: 'YOUR_SOLAR_ECLIPSE_LEO_EXTENDED_VIDEO_ID'
  },
  'neptune-saturn-conjunction': {
    title: 'Neptune-Saturn Conjunction',
    date: '',
    freeVideoId: 'YOUR_NEPTUNE_SATURN_FREE_VIDEO_ID',
    extendedVideoId: 'YOUR_NEPTUNE_SATURN_EXTENDED_VIDEO_ID'
  },
  'uranus-gemini': {
    title: 'Uranus Enters Gemini — The 84-Year Door Opens',
    date: 'April 25-26, 2026',
    freeVideoId: null,
    extendedVideoId: null,
    signSpecificVideoIds: {
      aries: 'LKZV4RJo598', taurus: 'LKZV4RJo598', gemini: 'LKZV4RJo598',
      cancer: 'LKZV4RJo598', leo: 'LKZV4RJo598', virgo: 'LKZV4RJo598',
      libra: 'LKZV4RJo598',
      scorpio: 'q_dbAryBQoM', sagittarius: 'q_dbAryBQoM', capricorn: 'q_dbAryBQoM',
      aquarius: 'q_dbAryBQoM', pisces: 'q_dbAryBQoM'
    }
  },
  'jupiter-leo': {
    title: 'Jupiter in Leo',
    date: '',
    freeVideoId: 'YOUR_JUPITER_LEO_FREE_VIDEO_ID',
    extendedVideoId: 'YOUR_JUPITER_LEO_EXTENDED_VIDEO_ID'
  },
  'nodes-changing': {
    title: 'Nodes changing signs',
    date: '',
    freeVideoId: 'YOUR_NODES_CHANGING_FREE_VIDEO_ID',
    extendedVideoId: 'YOUR_NODES_CHANGING_EXTENDED_VIDEO_ID'
  },
  'venus-retrograde': {
    title: 'Venus Retrograde',
    date: '',
    freeVideoId: 'YOUR_VENUS_RETROGRADE_FREE_VIDEO_ID',
    extendedVideoId: 'YOUR_VENUS_RETROGRADE_EXTENDED_VIDEO_ID'
  }
};

const signColors = {
  aries: '#FF6B6B',
  taurus: '#4ECDC4',
  gemini: '#FFE66D',
  cancer: '#A8E6CF',
  leo: '#FFD93D',
  virgo: '#95E1D3',
  libra: '#FFA8B5',
  scorpio: '#B983FF',
  sagittarius: '#FF8C42',
  capricorn: '#6C5B7B',
  aquarius: '#4ECDC4',
  pisces: '#C8A2C8'
};

const EventPage = () => {
  const { sign, event } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdminMode = searchParams.get('admin') === 'true';
  const color = signColors[sign?.toLowerCase()] || '#667eea';
  const currentEvent = eventData[event];
  const freeVideoId = currentEvent?.signSpecificVideoIds?.[sign?.toLowerCase()] || currentEvent?.freeVideoId;

  if (!currentEvent) {
    return (
      <Box sx={{ bgcolor: '#0a0a0f', minHeight: '100vh', p: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="h4">Event not found</Typography>
          <Button onClick={() => navigate(`/rising/${sign}${isAdminMode ? '?admin=true' : ''}`)}>
            Back to {sign} Rising
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#0a0a0f', minHeight: '100vh' }}>
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${color}22 0%, ${color}44 100%)`,
          py: 6,
          borderBottom: `4px solid ${color}`
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, textTransform: 'capitalize' }}>
            {sign} Rising
          </Typography>
          <Typography variant="h5" color="text.secondary" sx={{ mb: 1 }}>
            {currentEvent.title}
          </Typography>
          {currentEvent.date && (
            <Typography variant="h6" color="text.secondary">
              {currentEvent.date}
            </Typography>
          )}
          <Button
            variant="outlined"
            onClick={() => navigate(`/rising/${sign}${isAdminMode ? '?admin=true' : ''}`)}
            sx={{ mt: 3, borderColor: color, color: color }}
          >
            ← Back to {sign} Rising Overview
          </Button>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CalendarTodayIcon sx={{ color: color, mr: 1, fontSize: 32 }} />
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {currentEvent.title}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
              <Chip
                label="Free Overview"
                size="small"
                sx={{ bgcolor: '#4CAF50', color: 'white', fontWeight: 600 }}
              />
              <Chip
                label="Extended Available"
                size="small"
                sx={{ bgcolor: color, color: 'white', fontWeight: 600 }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Free Video */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <PlayCircleIcon sx={{ color: '#4CAF50', mr: 1, fontSize: 32 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Free Overview Video
            </Typography>
          </Box>
          <Paper sx={{ p: 2, bgcolor: 'black' }}>
            <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
              <ReactPlayer
                url={`https://youtube.com/watch?v=${freeVideoId}`}
                width="100%"
                height="100%"
                style={{ position: 'absolute', top: 0, left: 0 }}
                controls={true}
              />
            </Box>
          </Paper>
        </Paper>

        {/* Extended 2026 Cosmic Overview */}
        <Card sx={{ boxShadow: 4 }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LockIcon sx={{ color: color, mr: 1, fontSize: 32 }} />
                <Typography variant="h4" sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
                }}>
                  Extended 2026 Cosmic Overview
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="h3" sx={{
                  color: color,
                  fontWeight: 700,
                  fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' }
                }}>
                  $29.99
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  One-time payment
                </Typography>
              </Box>
            </Box>

            <Typography color="text.secondary" paragraph sx={{ fontSize: { xs: '1rem', md: '1.125rem' } }}>
              Get the complete 2026 forecast for {sign?.charAt(0).toUpperCase() + sign?.slice(1)} Rising with:
            </Typography>

            <Box component="ul" sx={{ mb: 3, pl: 2, '& li': { mb: 1, fontSize: { xs: '0.95rem', md: '1.05rem' } } }}>
              <li>90+ Minute Video Deep Dive</li>
              <li>House by House Transits & Degrees impacted</li>
              <li>Month by month timing guidance for major transits</li>
              <li>Comprehensive PDF Report (request the written format FREE)</li>
              <li>Birth Chart Wheel with visual aspects</li>
              <li>Key Activation Windows</li>
              <li>House activations</li>
              <li>Lifetime access</li>
            </Box>

            <Button
              variant="contained"
              size="large"
              fullWidth
              sx={{
                py: { xs: 2, md: 2.5 },
                mb: 2,
                fontSize: { xs: '1rem', md: '1.25rem' },
                bgcolor: color,
                '&:hover': { bgcolor: color, opacity: 0.9 }
              }}
              onClick={() => navigate('/pricing?section=forecasts')}
            >
              View Pricing Options
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" display="block" sx={{ mt: 0.5, fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                30-Day Money-Back Guarantee
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default EventPage;
